import Component from '@glimmer/component';
import { guidFor } from '@ember/object/internals';
import { assert } from '@ember/debug';
import { concat } from '@ember/helper';
import { on } from '@ember/modifier';
import { eq } from 'ember-truth-helpers';
import { t } from 'ember-intl';
import {
  HdsFormToggleField,
  HdsFormSelectField,
} from '@hashicorp/design-system-components/components';
import cloudIamRbacRoles, {
  ROLE_KEY_VIEWER,
} from 'core/utils/cloud-iam-rbac-roles';
import { Flex } from 'core/components';
import rbacRoles from 'core/helpers/rbac-roles';
import { TYPE_ORGANIZATION } from 'common/utils/cloud-resource-types';

const cloudIamRbacRoleValues = cloudIamRbacRoles
  .filter((role) => {
    // Find all organization role values.
    return role.type === TYPE_ORGANIZATION;
  })
  .map((role) => {
    // Get only the values.
    return role.value;
  });

/**
 *
 * `IamInvitationsFormBasicRoleSelect` renders a toggle and a select and exposes
 * a callback function to keep track of a value for selecting a basic role for
 * a user. This only renders basic roles and is not intended for use with
 * fine-grained access control.
 *
 *
 * ```
 * <Iam::Invitations::Form::BasicRoleSelect
 *   @onChange={{this.onChange}}
 * />
 * ```
 *
 * @class IamInvitationsFormBasicRoleSelect
 *
 */
export default class IamInvitationsFormBasicRoleSelect extends Component {
  get id() {
    return guidFor(this);
  }

  get roleId() {
    const { roleId } = this.args;

    if (roleId) {
      assert(
        `@roleId for "Iam::Invitations::Form::BasicRoleSelect" must one of the following: ${cloudIamRbacRoleValues.join(
          ', '
        )}, received ${roleId}`,
        cloudIamRbacRoleValues.includes(roleId)
      );
    }

    return roleId;
  }

  get defaultRoleValue() {
    const { defaultRoleValue = ROLE_KEY_VIEWER } = this.args;
    return defaultRoleValue;
  }

  onToggleChange = (e) => {
    const checked = e.target.checked;
    if (checked) {
      this.onChange(this.defaultRoleValue);
    } else {
      this.onChange(null);
    }
  };

  onSelectChange = (e) => {
    this.onChange(e.target.value);
  };

  /**
   * The callback function when either the toggle or the select changes.
   * @argument onChange
   * @type {Function}
   */
  onChange = (roleId) => {
    let { onChange } = this.args;

    const role =
      roleId &&
      cloudIamRbacRoles.find((role) => {
        return role.value === roleId;
      });

    if (typeof onChange === 'function') {
      if (!role) {
        return onChange({ value: undefined });
      }

      return onChange({ value: roleId });
    }
  };

  <template>
    <Flex
      @direction="column"
      @alignItems="start"
      @gap="md"
      data-test-basic-role-select
    >
      <HdsFormToggleField
        name="assign-user-role"
        checked={{if this.roleId true false}}
        {{on "change" this.onToggleChange}}
        data-test-basic-role-select-toggle-field
        as |F|
      >
        <F.Label>{{t
            "components.iam.invitations.form.basic-role-select.assign-this-user-an-org-role"
          }}</F.Label>
        <F.HelperText>{{t
            "components.iam.invitations.form.basic-role-select.users-without-org-roles-cannot-view"
          }}</F.HelperText>
      </HdsFormToggleField>
      <Flex @direction="row" @alignItems="end" @gap="md">
        {{#if this.roleId}}
          <HdsFormSelectField
            @extraAriaDescribedBy={{concat
              "helper-text-user-role-selector-"
              this.id
            }}
            @id={{concat "user-role-selector-" this.id}}
            data-test-basic-role-select-field
            {{on "change" this.onSelectChange}}
            ...attributes
            as |F|
          >
            <F.Label>
              {{t
                "components.iam.invitations.form.basic-role-select.select-an-org-role"
              }}
            </F.Label>
            <F.Options>
              {{#each (rbacRoles active=true) as |role|}}
                {{#if (eq role.value this.roleId)}}
                  <option selected value={{role.value}}>
                    {{role.label}}
                  </option>
                {{else}}
                  <option value={{role.value}}>
                    {{role.label}}
                  </option>
                {{/if}}
              {{/each}}
            </F.Options>
          </HdsFormSelectField>
        {{/if}}
        {{#if (has-block "afterSelect")}}
          <Flex @shrink={{0}} @grow={{1}}>
            {{yield to="afterSelect"}}
          </Flex>
        {{/if}}
      </Flex>
    </Flex>
  </template>
}
